import { marked } from "marked";

const HELP_MARKDOWN = import.meta.glob<string>(
  "../../../content/parishes/*/help.md",
  { query: "?raw", import: "default", eager: true },
);

const HELP_IMAGES = import.meta.glob<string>(
  "../../../content/parishes/*/images/*",
  { eager: true, import: "default" },
);

export interface HelpSection {
  id: string;
  title: string;
  html: string;
}

export interface ParishHelp {
  slug: string;
  parishName: string;
  introHtml: string;
  sections: HelpSection[];
}

interface ParsedFrontmatter {
  data: Record<string, string>;
  body: string;
}

function parseFrontmatter(raw: string): ParsedFrontmatter {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!match) return { data: {}, body: raw };
  const data: Record<string, string> = {};
  for (const line of match[1].split("\n")) {
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const key = line.slice(0, colon).trim();
    const value = line.slice(colon + 1).trim().replace(/^["']|["']$/g, "");
    if (key) data[key] = value;
  }
  return { data, body: match[2] };
}

function slugify(text: string): string {
  // Strip quotes/apostrophes before the non-alphanumeric replace so
  // "can't" → "cant", not "can-t". Covers straight + curly variants
  // and backtick. Anything else non-alphanumeric still collapses to
  // a single hyphen below.
  return text
    .toLowerCase()
    .replace(/['"‘’“”`]/g, "")
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface RawSection {
  title: string;
  body: string;
}

function splitSections(body: string): { intro: string; sections: RawSection[] } {
  const lines = body.split("\n");
  let intro = "";
  const sections: RawSection[] = [];
  let current: RawSection | null = null;
  let inFence = false;
  let skipTopH1 = true;

  for (const line of lines) {
    if (/^```/.test(line)) inFence = !inFence;

    if (!inFence && skipTopH1 && /^#\s+/.test(line)) {
      skipTopH1 = false;
      continue;
    }
    skipTopH1 = false;

    const h2 = !inFence && /^##\s+(.+?)\s*$/.exec(line);
    if (h2) {
      if (current) sections.push(current);
      current = { title: h2[1].trim(), body: "" };
      continue;
    }

    if (current) {
      current.body += (current.body ? "\n" : "") + line;
    } else {
      intro += (intro ? "\n" : "") + line;
    }
  }
  if (current) sections.push(current);

  return { intro: intro.trim(), sections };
}

function rewriteImagePaths(html: string, slug: string): string {
  return html.replace(
    /(<img\b[^>]*\bsrc=["'])\.\/images\/([^"']+)(["'][^>]*>)/g,
    (match, prefix, file, suffix) => {
      const url = lookupImageUrl(slug, file);
      if (!url) return match;
      return `${prefix}${url}${suffix}`;
    },
  );
}

function lookupImageUrl(slug: string, filename: string): string | null {
  for (const [key, url] of Object.entries(HELP_IMAGES)) {
    if (key.endsWith(`/parishes/${slug}/images/${filename}`)) {
      return url;
    }
  }
  return null;
}

function renderMarkdown(md: string): string {
  return marked.parse(md, { async: false, gfm: true, breaks: false }) as string;
}

function buildHelp(slug: string, raw: string): ParishHelp {
  const { data, body } = parseFrontmatter(raw);
  const { intro, sections } = splitSections(body);
  const parishName = data.parish || slug;

  const introHtml = intro ? rewriteImagePaths(renderMarkdown(intro), slug) : "";
  const seen = new Set<string>();
  const built: HelpSection[] = sections.map((s) => {
    let id = slugify(s.title) || "section";
    let candidate = id;
    let n = 2;
    while (seen.has(candidate)) {
      candidate = `${id}-${n}`;
      n += 1;
    }
    seen.add(candidate);
    return {
      id: candidate,
      title: s.title,
      html: rewriteImagePaths(renderMarkdown(s.body), slug),
    };
  });

  return { slug, parishName, introHtml, sections: built };
}

const CACHE = new Map<string, ParishHelp | null>();

export function loadParishHelp(slug: string): ParishHelp | null {
  if (CACHE.has(slug)) return CACHE.get(slug) ?? null;
  let found: ParishHelp | null = null;
  for (const [key, raw] of Object.entries(HELP_MARKDOWN)) {
    if (key.endsWith(`/parishes/${slug}/help.md`)) {
      found = buildHelp(slug, raw);
      break;
    }
  }
  CACHE.set(slug, found);
  return found;
}
